import { ColDef } from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import { Box } from '@atgfr8/atg-ui-tailwind';
import { BasicModal, Button } from '@components';
import {
  columnsToolPanel,
  filtersToolPanel,
} from '@components/Grid/components/constants';
import { dateComparator } from '@store/loadboard/utils';
import { LTLCatalogs } from '@typeDefinitions/ltlTypes';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { caseInsensitiveComparator } from '@views/billingQueues/components/utils';
import { atgLogo } from '@views/ltl';
import { DateTime } from 'luxon';
import { Show, createSignal } from 'solid-js';

import {
  AddEditProductDetails,
  ImportCatalogItem,
  ProductCatalogActionsButton,
} from './components';
import { fetchProductCatalog } from './services';

interface LTLProductCatalogPopupProps {
  customerId: number;
  goBack?: () => void;
  onRowClick?: (rowData: LTLCatalogs) => void;
  goBackButtonText?: string;
  fromCustomerDetails?: boolean;
  closeModal: (modalId: string) => void;
  openModal: (modalId: string) => void;
  changeTrackingSources?: {
    add?: ChangeTrackingSource;
    update?: ChangeTrackingSource;
    delete?: ChangeTrackingSource;
    import?: ChangeTrackingSource;
  };
}

const agGridCellStyle = {
  padding: '10px',
  'word-break': 'break-word',
  'white-space': 'normal',
  'line-height': '1.6 ',
  'align-items': 'center !important',
};

const LTLProductCatalog = (props: LTLProductCatalogPopupProps) => {
  const [gridRef, setGridRef] = createSignal<AgGridSolidRef | null>(null);
  const [selectedRow, setSelectedRow] = createSignal<LTLCatalogs | null>(null);
  const [edit, setEdit] = createSignal(false);
  const [failedCsv, setFailedCsv] = createSignal<string | null>(null);

  const filterParams = {
    filterOptions: [
      'equals',
      'notEqual',
      'contains',
      'startsWith',
      'endsWith',
      'notContains',
    ],
    buttons: ['apply', 'reset'],
    suppressAndOrCondition: true,
  };

  const columnDefs: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      minWidth: 220,
      cellStyle: agGridCellStyle,
      autoHeight: true,
      filter: 'agTextColumnFilter',
      filterParams,
      comparator: caseInsensitiveComparator,
    },
    {
      headerName: 'Description',
      field: 'description',
      minWidth: 220,
      cellStyle: agGridCellStyle,
      autoHeight: true,
      filter: 'agTextColumnFilter',
      filterParams,
      comparator: caseInsensitiveComparator,
    },
    {
      headerName: 'Class',
      field: 'class',
      cellStyle: agGridCellStyle,
      autoHeight: true,
      filter: 'agTextColumnFilter',
      filterParams,
      minWidth: 90,
    },
    {
      headerName: 'NMFC',
      field: 'nmfc',
      cellStyle: agGridCellStyle,
      autoHeight: true,
      filter: 'agTextColumnFilter',
      filterParams,
      minWidth: 100,
    },
    {
      headerName: 'Note',
      field: 'note',
      cellStyle: agGridCellStyle,
      autoHeight: true,
      sortable: false,
      resizable: true,
      filter: 'agTextColumnFilter',
      filterParams,
      minWidth: 100,
      cellRenderer: (params: { value: string }) => {
        return (
          <span class="ellipsis-cell" title={params.value}>
            {params.value}
          </span>
        );
      },
    },
    {
      headerName: 'Dimensions',
      cellStyle: agGridCellStyle,
      autoHeight: true,
      filter: false,
      sortable: false,
      valueFormatter: (params) => {
        const data = params.data as LTLCatalogs;
        return `${data.lengthInches || ''}" x ${data.widthInches || ''}" x ${
          data.heightInches || ''
        }"`;
      },
      minWidth: 120,
    },
    {
      headerName: 'Weight',
      field: 'weight',
      cellStyle: agGridCellStyle,
      autoHeight: true,
      filter: 'agTextColumnFilter',
      filterParams,
      minWidth: 100,
    },
    {
      headerName: 'Hazmat',
      field: 'hazmat',
      cellRenderer: (params: { value: boolean }) => {
        return params.value ? 'true' : 'false';
      },
      cellStyle: agGridCellStyle,
      autoHeight: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        ...filterParams,
        values: ['true', 'false'],
      },
      minWidth: 70,
    },
    {
      headerName: 'Unit Type',
      field: 'unitType',
      cellStyle: agGridCellStyle,
      autoHeight: true,
      filter: 'agTextColumnFilter',
      filterParams,
      minWidth: 100,
      comparator: caseInsensitiveComparator,
    },
    {
      headerName: 'Created',
      field: 'createdDate',
      cellStyle: agGridCellStyle,
      autoHeight: true,
      filter: 'agDateColumnFilter',
      filterParams: {
        comparator: dateComparator,
        filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
        suppressAndOrCondition: true,
        buttons: ['apply', 'reset'],
        minValidYear: 1900,
        maxValidYear: new Date().getFullYear() + 1,
      },
      valueFormatter: (params) => {
        const date = DateTime.fromISO(params.value as string);
        if (date.isValid) {
          return `${date.setZone('local').toFormat('MM/dd/yyyy')}`;
        }
        return '';
      },
      minWidth: 100,
    },
  ];

  const onModalClose = () => {
    setEdit(false);
    setSelectedRow(null);
    props.closeModal('addEditProductDetails');
  };

  const handleUpdateSuccess = (row: LTLCatalogs) => {
    gridRef()?.api.applyTransaction({ update: [row] });
  };

  const handleAddSuccess = (row: LTLCatalogs) => {
    gridRef()?.api.applyTransaction({ add: [row], addIndex: 0 });
  };

  const handleDeleteSuccess = (id: string) => {
    const rowNode = gridRef()?.api.getRowNode(String(id));
    if (rowNode?.data) {
      gridRef()?.api.applyTransaction({ remove: [rowNode.data] });
    }
  };

  const isFromCustomerDetails = props.fromCustomerDetails ?? false;

  return (
    <>
      <Show when={Boolean(props.fromCustomerDetails)}>
        <ProductCatalogActionsButton
          setEdit={setEdit}
          openModal={props.openModal}
          gridRef={gridRef}
        />
      </Show>
      <Box
        class="ag-theme-alpine flex-1!"
        style={{ height: isFromCustomerDetails ? 'auto' : '550px' }}
      >
        <AgGridSolid
          overlayNoRowsTemplate="No data available"
          overlayLoadingTemplate={atgLogo}
          ref={setGridRef}
          columnDefs={columnDefs}
          onRowClicked={(params: { data: LTLCatalogs }) => {
            //Access from Customer Details
            if (Boolean(props.fromCustomerDetails)) {
              setSelectedRow(params.data);
              setEdit(true);
              props.openModal('addEditProductDetails');
            } else {
              props.onRowClick && props.onRowClick(params.data);
            }
          }}
          gridOptions={{
            defaultColDef: {
              flex: 1,
            },
            getRowId: (params) => String((params.data as LTLCatalogs).id),
            pagination: true,

            suppressPaginationPanel: false,

            paginationPageSize: 100,
            cacheBlockSize: 100,
            sideBar: {
              toolPanels: [columnsToolPanel, filtersToolPanel],
              defaultToolPanel: '',
            },
            paginationPageSizeSelector: false,
            onGridReady: async (gridParams) => {
              gridParams.api.showLoadingOverlay();
              const res = await fetchProductCatalog(props.customerId);
              gridParams.api.setGridOption('rowData', res.data);
              gridParams.api.hideOverlay();
            },
          }}
        />
      </Box>
      {props.goBack && (
        <Box display="flex" justifyContent="end" alignItems="center" pt={2}>
          <Button
            size="md"
            onClick={props.goBack}
            label={props.goBackButtonText}
            dataTestId={formatDataTestId(
              props.goBackButtonText ?? 'Go Back',
              'button',
            )}
          />
        </Box>
      )}
      <BasicModal
        id="addEditProductDetails"
        title="Product Catalog"
        width="800px"
        maxWidth="800px"
        footer={false}
        onClose={onModalClose}
      >
        <AddEditProductDetails
          customerId={props.customerId}
          edit={edit}
          setEdit={setEdit}
          selectedRow={selectedRow}
          onModalClose={onModalClose}
          onAddSuccess={handleAddSuccess}
          onUpdateSuccess={handleUpdateSuccess}
          onDeleteSuccess={handleDeleteSuccess}
          addChangeTrackingSource={props.changeTrackingSources?.add}
          updateChangeTrackingSource={props.changeTrackingSources?.update}
          deleteChangeTrackingSource={props.changeTrackingSources?.delete}
        />
      </BasicModal>
      <BasicModal
        id="importProductCatalog"
        title="Catalog Item Import"
        width="600px"
        maxWidth="600px"
        footer={false}
        onClose={() => {
          setFailedCsv(null);
        }}
      >
        <ImportCatalogItem
          gridRef={gridRef}
          closeModal={props.closeModal}
          failedCsv={failedCsv}
          setFailedCsv={setFailedCsv}
          importChangeTrackingSource={props.changeTrackingSources?.import}
        />
      </BasicModal>
    </>
  );
};

export default LTLProductCatalog;
